import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import getWishlistSummary from '../../api/wishlist/get-wishlist-summary';
import API_URLS from '../../constants/api-urls';
import { world } from '../../languages';
import anonymousPersist from '../../utils/anonymous-persist';
import { wishlistKeys } from './query-keys';

// Wishlist counts/hearts must always look fresh — "fresh" data class (staleTime 0).
const STALE_TIME = 0;
const GC_TIME = 5 * 60_000;

type UseWishlistProps = { isLoggedIn: boolean; enabled?: boolean };
type UseWishlistReturn = { ids: number[]; total: number; isLoading: boolean; isSuccess: boolean };

const fetchWishlist = async (isLoggedIn: boolean) => {
    if (isLoggedIn) {
        await anonymousPersist();

        return getWishlistSummary(`${API_URLS.WISHLIST}/query`);
    }

    return getWishlistSummary(`${API_URLS.ANONYMOUS_WISHLIST}/query`);
};

const useWishlist = ({ isLoggedIn, enabled = true }: UseWishlistProps): UseWishlistReturn => {
    const auth = isLoggedIn ? 'user' : 'anonymous';

    const { data, isLoading, isSuccess } = useQuery({
        queryKey: wishlistKeys.list(auth, world),
        queryFn: () => fetchWishlist(isLoggedIn),
        enabled,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        refetchOnWindowFocus: false // TODO FE-251: drop once the global default lands
    });

    const ids = useMemo(() => data?.list.map((item) => item.id) ?? [], [data]);

    return {
        ids,
        total: data?.total ?? 0,
        isLoading,
        isSuccess
    };
};

export default useWishlist;
